import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { User, Mail, Phone, MapPin, Shield, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const ContactForm = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Fixed math question for spam control
  const [mathQuestion] = useState(() => {
    return { num1: 10, num2: 4, answer: 14 };
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    setSubmitStatus(null);

    // Validate spam control
    if (parseInt(data.captchaAnswer) !== mathQuestion.answer) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Incorrect answer to the math question. Please try again.' 
      });
      setSubmitting(false);
      return;
    }

    try {
      // Remove captcha answer before sending to API
      const { captchaAnswer, ...submitData } = data;
      
      // Get API endpoint from environment variable or use placeholder
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'YOUR_API_GATEWAY_ENDPOINT';
      
      const response = await axios.post(apiEndpoint, submitData);

      if (response.status === 200) {
        setSubmitStatus({ type: 'success', message: 'Thank you! We\'ve received your information and will be in touch soon.' });
        reset();
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'There was an error submitting your information. Please try again or contact us directly at coji@myprivatelane.com' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-xl border border-gray-700">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name Field */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <User className="w-4 h-4" />
            Full Name *
          </label>
          <input
            type="text"
            {...register('name', { required: 'Name is required' })}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            placeholder="John Doe"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Address *
          </label>
          <input
            type="email"
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            placeholder="john@example.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Phone Number *
          </label>
          <input
            type="tel"
            {...register('phone', { 
              required: 'Phone number is required',
              pattern: {
                value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
                message: 'Invalid phone number'
              }
            })}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            placeholder="+1 (555) 123-4567"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>
          )}
        </div>

        {/* City Field */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            City *
          </label>
          <input
            type="text"
            {...register('city', { required: 'City is required' })}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            placeholder="New York"
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-400">{errors.city.message}</p>
          )}
        </div>

        {/* State Field */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            State *
          </label>
          <input
            type="text"
            {...register('state', { required: 'State is required' })}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            placeholder="NY"
          />
          {errors.state && (
            <p className="mt-1 text-sm text-red-400">{errors.state.message}</p>
          )}
        </div>

        {/* Spam Control - Math Question */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-400" />
            Spam Protection: What is {mathQuestion.num1} + {mathQuestion.num2}? *
          </label>
          <input
            type="number"
            {...register('captchaAnswer', { 
              required: 'Please answer the math question',
              valueAsNumber: true
            })}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            placeholder="Your answer"
          />
          {errors.captchaAnswer && (
            <p className="mt-1 text-sm text-red-400">{errors.captchaAnswer.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-6 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit & Get Started'
          )}
        </button>

        {/* Status Messages */}
        {submitStatus && (
          <div className={`p-4 rounded-lg flex items-start gap-3 ${
            submitStatus.type === 'success' 
              ? 'bg-green-900/30 border border-green-500/50' 
              : 'bg-red-900/30 border border-red-500/50'
          }`}>
            {submitStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            )}
            <p className={`text-sm ${
              submitStatus.type === 'success' ? 'text-green-100' : 'text-red-100'
            }`}>
              {submitStatus.message}
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
